import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle, MapPin, Waves, Camera, Users2, Compass, Plane, Sparkles, Calendar, Plus, Trash2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { publicApi } from "@/lib/api";
import { toast } from "sonner";

const DESTINATIONS = [
  { key: "port_blair", label: "Port Blair", icon: MapPin },
  { key: "havelock", label: "Havelock (Swaraj Dweep)", icon: Waves },
  { key: "neil_island", label: "Neil Island (Shaheed Dweep)", icon: Compass },
  { key: "baratang", label: "Baratang Limestone Caves", icon: Camera },
  { key: "ross_island", label: "Ross Island", icon: MapPin },
  { key: "diglipur", label: "Diglipur / North Andaman", icon: Compass },
];

const INTERESTS = [
  "Beach relaxation", "Scuba diving", "Snorkeling", "Sea walking",
  "Water sports (jet ski, parasailing)", "Historical sites (Cellular Jail)",
  "Photography", "Trekking / Hiking", "Local cuisine", "Sunset cruises",
];

const BUDGET_RANGES = [
  { key: "budget", label: "Budget (< ₹25,000 per person)" },
  { key: "mid", label: "Mid-range (₹25,000 – ₹50,000 per person)" },
  { key: "premium", label: "Premium (₹50,000 – ₹1,00,000 per person)" },
  { key: "luxury", label: "Luxury (₹1,00,000+ per person)" },
];

const CustomizeTour = () => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    duration: "6", adults: 2, children: 0,
    travel_date: "", budget: "",
    accommodation: "3-star",
    destinations: [],
    interests: [],
    itinerary: [],
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const toggle = (arrKey, value) => {
    setForm((f) => ({
      ...f,
      [arrKey]: f[arrKey].includes(value)
        ? f[arrKey].filter((x) => x !== value)
        : [...f[arrKey], value],
    }));
  };

  const addDay = () => {
    setForm((f) => ({
      ...f,
      itinerary: [...f.itinerary, { day: f.itinerary.length + 1, title: "", description: "" }],
    }));
  };
  const updateDay = (idx, key, value) => {
    setForm((f) => ({
      ...f,
      itinerary: f.itinerary.map((d, i) => (i === idx ? { ...d, [key]: value } : d)),
    }));
  };
  const removeDay = (idx) => {
    setForm((f) => ({
      ...f,
      itinerary: f.itinerary.filter((_, i) => i !== idx).map((d, i) => ({ ...d, day: i + 1 })),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please enter your name and phone number");
      return;
    }
    setSubmitting(true);
    const summary = [
      `Duration: ${form.duration} days`,
      `Travellers: ${form.adults} adults${form.children ? ` + ${form.children} children` : ""}`,
      form.travel_date ? `Preferred date: ${form.travel_date}` : null,
      form.budget ? `Budget: ${BUDGET_RANGES.find(b => b.key === form.budget)?.label}` : null,
      `Accommodation: ${form.accommodation}`,
      form.destinations.length ? `Destinations: ${form.destinations.map(k => DESTINATIONS.find(d => d.key === k)?.label).join(", ")}` : null,
      form.interests.length ? `Interests: ${form.interests.join(", ")}` : null,
      form.itinerary.length ? `\nCUSTOMER-DRAFTED ITINERARY:\n${form.itinerary.map(d => `  Day ${d.day}${d.title ? ` — ${d.title}` : ""}${d.description ? `: ${d.description}` : ""}`).join("\n")}` : null,
      form.notes ? `Notes: ${form.notes}` : null,
    ].filter(Boolean).join("\n");

    try {
      await publicApi.post("/inquiries/quote", {
        name: form.name,
        phone: form.phone,
        email: form.email || undefined,
        package_interest: "Custom Itinerary",
        travel_date: form.travel_date || null,
        message: `CUSTOM TOUR REQUEST\n\n${summary}`,
      });
      setDone(true);
      toast.success("Request received! Our travel expert will call you within 2 hours.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Failed to submit. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="customize-tour-page" className="grain-texture min-h-screen">
      <SEO
        title="Customize Your Andaman Tour"
        description="Design your own Andaman itinerary — pick your destinations, duration, budget and activities. Our travel experts craft a personalised plan within 2 hours."
        path="/customize-tour"
      />
      <Navbar />

      <section className="pt-32 pb-8 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="accent-label text-secondary">DESIGN YOUR OWN TRIP</span>
          <h1 className="text-4xl sm:text-5xl font-serif mt-3 text-white">Customize Your Andaman Tour</h1>
          <p className="text-white/80 mt-3 max-w-2xl mx-auto">
            Tell us how you like to travel — we'll craft a personalised itinerary within 2 hours with best prices from our partner hotels &amp; resorts.
          </p>
        </div>
      </section>

      <section className="py-16 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {done ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="admin-card text-center" data-testid="customize-success">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="text-green-600" size={32} />
              </div>
              <h2 className="text-3xl font-serif text-primary mb-3">Request received!</h2>
              <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
                Our senior travel consultant will call you within <strong>2 hours</strong> on <strong>{form.phone}</strong> with a personalised itinerary and best-price quote.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => window.location.href = "/packages"} className="bg-primary hover:bg-primary/90">
                  Browse ready-made packages
                </Button>
                <Button variant="outline" onClick={() => window.location.href = "https://wa.me/919933237121"}>
                  WhatsApp us now
                </Button>
              </div>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="customize-form" className="space-y-8">
              {/* Contact block */}
              <div className="admin-card">
                <h2 className="text-2xl font-serif text-primary mb-1 flex items-center gap-2">
                  <Users2 size={22} /> Who are we designing for?
                </h2>
                <p className="text-muted-foreground text-sm mb-5">We'll only use these details to send you the itinerary.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Your name *</label>
                    <Input data-testid="cust-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone / WhatsApp *</label>
                    <Input data-testid="cust-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium mb-1">Email (optional)</label>
                    <Input data-testid="cust-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="For the emailed PDF itinerary" />
                  </div>
                </div>
              </div>

              {/* Trip block */}
              <div className="admin-card">
                <h2 className="text-2xl font-serif text-primary mb-5 flex items-center gap-2">
                  <Plane size={22} /> Trip basics
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Duration (days)</label>
                    <select data-testid="cust-duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                      {[3, 4, 5, 6, 7, 8, 9, 10, 12, 14].map(d => <option key={d} value={d}>{d} days</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Adults</label>
                    <Input data-testid="cust-adults" type="number" min="1" value={form.adults} onChange={(e) => setForm({ ...form, adults: parseInt(e.target.value) || 1 })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Children</label>
                    <Input data-testid="cust-children" type="number" min="0" value={form.children} onChange={(e) => setForm({ ...form, children: parseInt(e.target.value) || 0 })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Preferred travel date</label>
                    <Input data-testid="cust-date" type="date" min={new Date().toISOString().split("T")[0]}
                      value={form.travel_date} onChange={(e) => setForm({ ...form, travel_date: e.target.value })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Accommodation</label>
                    <select data-testid="cust-accom" value={form.accommodation} onChange={(e) => setForm({ ...form, accommodation: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                      <option value="3-star">3-star (comfortable, budget-friendly)</option>
                      <option value="4-star">4-star (premium, sea view where possible)</option>
                      <option value="5-star">5-star luxury (Taj / Barefoot / Munjoh)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Budget</label>
                    <select data-testid="cust-budget" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                      <option value="">Not sure yet</option>
                      {BUDGET_RANGES.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Destinations block */}
              <div className="admin-card">
                <h2 className="text-2xl font-serif text-primary mb-1 flex items-center gap-2">
                  <MapPin size={22} /> Which destinations interest you?
                </h2>
                <p className="text-muted-foreground text-sm mb-5">Pick as many as you like — we'll optimise the ferry &amp; car route for you.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="cust-destinations">
                  {DESTINATIONS.map((d) => {
                    const active = form.destinations.includes(d.key);
                    return (
                      <button
                        key={d.key}
                        type="button"
                        onClick={() => toggle("destinations", d.key)}
                        data-testid={`dest-${d.key}`}
                        className={`flex items-center gap-3 p-3 rounded-lg border-2 text-left transition-all ${
                          active ? "border-secondary bg-secondary/10" : "border-border hover:border-primary/40"
                        }`}
                      >
                        <d.icon size={20} className={active ? "text-secondary" : "text-muted-foreground"} />
                        <span className={`text-sm font-medium ${active ? "text-primary" : ""}`}>{d.label}</span>
                        {active && <CheckCircle size={16} className="text-secondary ml-auto" />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Interests block */}
              <div className="admin-card">
                <h2 className="text-2xl font-serif text-primary mb-1 flex items-center gap-2">
                  <Sparkles size={22} /> What do you love doing on holiday?
                </h2>
                <p className="text-muted-foreground text-sm mb-5">This helps us pick the right activities and skip the tourist traps.</p>
                <div className="flex flex-wrap gap-2" data-testid="cust-interests">
                  {INTERESTS.map((i) => {
                    const active = form.interests.includes(i);
                    return (
                      <button
                        key={i}
                        type="button"
                        onClick={() => toggle("interests", i)}
                        data-testid={`interest-${i.toLowerCase().replace(/\s+/g, "-").replace(/[()]/g, "")}`}
                        className={`px-4 py-2 rounded-full text-sm border transition-all ${
                          active
                            ? "border-secondary bg-secondary text-secondary-foreground"
                            : "border-border hover:border-primary/40"
                        }`}
                      >
                        {i}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Day-wise itinerary builder */}
              <div className="admin-card">
                <h2 className="text-2xl font-serif text-primary mb-1 flex items-center gap-2">
                  <Calendar size={22} /> Draft your day-by-day plan (optional)
                </h2>
                <p className="text-muted-foreground text-sm mb-5">
                  Have a rough plan in mind? Add each day with a title &amp; what you'd like to do. Skip this if you'd rather have us design it fully.
                </p>
                <div className="space-y-3" data-testid="cust-itinerary">
                  {form.itinerary.map((d, idx) => (
                    <div key={idx} data-testid={`cust-day-${idx}`} className="border border-border rounded-lg p-4 bg-muted/30">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-sm font-semibold text-primary">Day {d.day}</span>
                        <button
                          type="button"
                          onClick={() => removeDay(idx)}
                          data-testid={`cust-day-remove-${idx}`}
                          className="text-red-600 hover:bg-red-50 p-1 rounded"
                          aria-label={`Remove day ${d.day}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <Input
                        data-testid={`cust-day-title-${idx}`}
                        value={d.title}
                        onChange={(e) => updateDay(idx, "title", e.target.value)}
                        placeholder="e.g. Arrival &amp; Cellular Jail visit"
                        className="mb-2"
                      />
                      <Textarea
                        data-testid={`cust-day-desc-${idx}`}
                        rows={2}
                        value={d.description}
                        onChange={(e) => updateDay(idx, "description", e.target.value)}
                        placeholder="What would you like to do this day?"
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={addDay}
                    data-testid="cust-add-day-btn"
                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-primary/30 rounded-lg text-primary hover:bg-primary/5 transition-colors"
                  >
                    <Plus size={16} /> Add Day {form.itinerary.length + 1}
                  </button>
                </div>
              </div>

              {/* Notes */}
              <div className="admin-card">
                <label className="block text-sm font-medium mb-2">Anything else we should know?</label>
                <Textarea
                  data-testid="cust-notes"
                  rows={4}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Anniversaries, food allergies, mobility needs, birthday surprises… nothing is too small."
                />
              </div>

              <Button
                type="submit"
                data-testid="cust-submit-btn"
                disabled={submitting}
                className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground h-14 text-lg"
              >
                {submitting ? "Sending your request…" : "Get my personalised itinerary"}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                No obligation, no spam. We'll call within 2 hours during 9 AM – 9 PM IST.
              </p>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CustomizeTour;
